import { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { toastModule } from "@/modules/toast";

import { createDataExport } from "../shared/api";

const Container = styled.div`
  padding: 32px;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Label = styled.label`
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
`;

const HelperText = styled.p`
  font-size: 13px;
  color: #6b7280;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;

export const DataExportsCreatePage = () => {
  const navigate = useNavigate();
  const [spaceAccountId, setSpaceAccountId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedSpaceAccountId = spaceAccountId.trim();

    if (!trimmedSpaceAccountId) {
      toastModule.triggerToast({ content: "Space account ID is required." });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createDataExport(trimmedSpaceAccountId);

      if (!response?.data_export) {
        throw new Error("Missing response payload.");
      }

      toastModule.triggerToast({ content: "Export created." });
      navigate(
        atlasRoutes.dataExportsView({ params: { dataExportId: response.data_export.id } }).path
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create export.";
      toastModule.triggerToast({ content: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Create Data Export</Title>
      <Form onSubmit={handleSubmit}>
        <div>
          <Label htmlFor="space-account-id">Space Account ID</Label>
          <Input
            id="space-account-id"
            placeholder="space-account UUID"
            value={spaceAccountId}
            onChange={event => setSpaceAccountId(event.target.value)}
            required
          />
          <HelperText>Exports will start immediately for the provided space account.</HelperText>
        </div>

        <ButtonRow>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create Export"}
          </Button>
          <Button
            type="button"
            variant="soft"
            onClick={() => navigate(atlasRoutes.dataExports({}).path)}
          >
            Cancel
          </Button>
        </ButtonRow>
      </Form>
    </Container>
  );
};
